/**
 * Copy text to the system clipboard with a graceful fallback.
 *
 * Returns:
 *   { ok: true }                     — modern clipboard API succeeded
 *   { ok: true, fallback: true }     — legacy execCommand("copy") succeeded
 *   { ok: false, manual: true, text } — neither path worked; caller should
 *                                       show a manual-copy prompt with `text`
 */
export interface CopyResult {
  ok: boolean;
  manual: boolean;
  fallback?: boolean;
  text?: string;
}

export async function copyTextWithFallback(text: string): Promise<CopyResult> {
  // 1. Modern Clipboard API (requires secure context + permission).
  try {
    if (
      typeof navigator !== "undefined" &&
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === "function"
    ) {
      await navigator.clipboard.writeText(text);
      return { ok: true, manual: false };
    }
  } catch {
    /* fall through to legacy path */
  }

  // 2. Legacy execCommand fallback for non-secure contexts / older browsers.
  if (typeof document !== "undefined") {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.top = "-1000px";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (ok) return { ok: true, manual: false, fallback: true };
    } catch {
      /* fall through */
    }
  }

  // 3. Nothing worked — caller should prompt the user to copy manually.
  return { ok: false, manual: true, text };
}